import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { morph } from '@theme/morph';
import { morphSection, sectionRenderer, normalizeSectionId, buildSectionSelector } from '@theme/section-renderer';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages the personalisation modal.
 *
 * @extends DialogComponent
 */
export class PersonaliseDialogComponent extends DialogComponent {
  requiredRefs = ['dialog', 'saveButton', 'closeButton'];
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.selectedFont = null;
    this.selectedColor = null;
    
    // Initialize personalisationData structure (fresh on each page load)
    this.personalisationData = {
      name: '',
      font: null,
      color: null,
      design: null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: null,
      kidName: null,
      mumName: null
    };
    
    // Set up direct click handlers for close buttons to stop propagation
    setTimeout(() => {
      this.#setupCloseButtonHandlers();
    }, 100);
    
    // Set up form submit listener
    setTimeout(() => {
      this.setupFormSubmitListenerForCurrentSession();
    }, 100);
    
    // Set form attribute on Baby/Kid/Mum inputs
    this.#setFormAttributeOnInputs();
    
    // Set up event listeners for input changes to update save button
    this.#setupInputChangeListeners();

    // Set up variant change listener for dynamic personalization (cb metafields)
    this.#setupCbVariantChangeListener();

    // Set up variant color change listener (when user selects color in modal)
    this.#setupVariantColorChangeListener();

    // If coming from collection page: auto-add ?customize=true to URL
    this.#maybeAddCustomizeParamFromCollection();
    // Auto-open personalisation when product page loads with ?customize=true
    this.#maybeAutoOpenPersonalisation();
  }

  /**
   * When navigating from a collection page to product page: adds ?customize=true to URL
   * @private
   */
  #maybeAddCustomizeParamFromCollection() {
    if (this.dataset.autoOpenPersonalisation !== 'true') return;
    if (this.dataset.context === 'cart-drawer') return;
    if (this.hasAttribute('data-quick-add')) return;

    const referrer = document.referrer || '';
    if (!referrer.includes('/collections/')) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('customize') === 'true') return;

    url.searchParams.set('customize', 'true');
    history.replaceState({}, '', url.toString());
  }

  /**
   * Auto-opens the personalisation modal when product page is loaded with ?customize=true
   * @private
   */
  #maybeAutoOpenPersonalisation() {
    if (this.dataset.autoOpenPersonalisation !== 'true') return;
    if (this.dataset.context === 'cart-drawer') return;
    if (this.hasAttribute('data-quick-add')) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('customize') !== 'true') return;

    if (window._personalisationAutoOpened) return;
    window._personalisationAutoOpened = true;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.refs?.dialog?.open) return;
        if (typeof this.showDialog === 'function') {
          this.showDialog();
        }
      }, 100);
    });
  }

  /**
   * Parses the cb personalization variants map from the embedded script
   * @returns {Object|null}
   */
  #getCbPersonalizationMap() {
    const script = this.querySelector('script[data-cb-personalization-variants]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Parses the cb personalization designs list from the embedded script
   * @returns {Object|null}
   */
  #getDesignsMap() {
    const script = this.querySelector('script[data-cb-personalisation-designs]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Updates the dynamic personalization preview when the selected variant changes.
   * @param {string} variantId - The selected variant ID
   */
  #updateCbPreviewForVariant(variantId) {
    const map = this.#getCbPersonalizationMap();
    if (!map) return;

    const variantKey = variantId != null ? String(variantId) : '';
    const { product, variants, variantFallbackImages } = map;
    let showDynamic = false;
    let imageUrl = '';
    let positionJson = '';

    if (variantKey && variants && variants[variantKey]?.image) {
      showDynamic = true;
      imageUrl = variants[variantKey].image;
      const pos = variants[variantKey].position;
      if (pos != null && pos !== 'null') {
        positionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
      }
    } else if (product?.image) {
      showDynamic = true;
      imageUrl = product.image;
      const pos = product.position;
      if (pos != null && pos !== 'null') {
        positionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
      }
    }

    const dynamicWrap = this.querySelector('[data-cb-dynamic-wrap]');
    const fallbackWrap = this.querySelector('[data-cb-fallback-wrap]');
    const placeholder = this.querySelector('[data-cb-placeholder]');

    if (showDynamic) {
      this.dataset.cbPersonalizationImage = imageUrl;
      this.dataset.cbPersonalizationPosition = positionJson;
      const dynamicImg = dynamicWrap?.querySelector('.personalise-modal__preview-image--dynamic');
      if (dynamicImg) {
        dynamicImg.src = imageUrl;
      }
      if (dynamicWrap) dynamicWrap.style.display = '';
      if (fallbackWrap) fallbackWrap.style.display = 'none';
      if (placeholder) placeholder.style.display = 'none';
      this.updateCbPreviewOverlay();
    } else {
      delete this.dataset.cbPersonalizationImage;
      delete this.dataset.cbPersonalizationPosition;
      if (dynamicWrap) dynamicWrap.style.display = 'none';
      if (fallbackWrap) {
        fallbackWrap.style.display = '';
        const fallbackImg = fallbackWrap.querySelector('.personalise-modal__preview-image');
        const fallbackImageUrl = variantFallbackImages?.[variantKey] || product?.fallbackImage;
        if (fallbackImg && fallbackImageUrl) {
          fallbackImg.src = fallbackImageUrl;
          fallbackImg.removeAttribute('srcset');
        }
      }
      if (placeholder && !fallbackWrap) placeholder.style.display = '';
    }

    let dobPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.dobPosition != null && variants[variantKey].dobPosition !== 'null') {
      const pos = variants[variantKey].dobPosition;
      dobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.dobPosition != null && product.dobPosition !== 'null') {
      const pos = product.dobPosition;
      dobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (dobPositionJson) {
      this.dataset.cbDobPersonalizationPosition = dobPositionJson;
    } else {
      delete this.dataset.cbDobPersonalizationPosition;
    }

    let tobPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.tobPosition != null && variants[variantKey].tobPosition !== 'null') {
      const pos = variants[variantKey].tobPosition;
      tobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.tobPosition != null && product.tobPosition !== 'null') {
      const pos = product.tobPosition;
      tobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (tobPositionJson) {
      this.dataset.cbTobPersonalizationPosition = tobPositionJson;
    } else {
      delete this.dataset.cbTobPersonalizationPosition;
    }

    let weightPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.weightPosition != null && variants[variantKey].weightPosition !== 'null') {
      const pos = variants[variantKey].weightPosition;
      weightPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.weightPosition != null && product.weightPosition !== 'null') {
      const pos = product.weightPosition;
      weightPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (weightPositionJson) {
      this.dataset.cbWeightPersonalizationPosition = weightPositionJson;
    } else {
      delete this.dataset.cbWeightPersonalizationPosition;
    }

    this.#updateCbOptionalFieldPreviewOverlays();
    this.#updateColorGridForVariant(variantKey, map);
    this.#clearInvalidColorOnVariantChange(variantKey, map);
  }

  /**
   * Clears saved color if invalid for new variant.
   * @private
   */
  #clearInvalidColorOnVariantChange(variantKey, map) {
    if (this.dataset.context === 'cart-drawer') return;

    const { variantColors = {}, productColors = [] } = map;
    const allowedColors = (variantKey && variantColors?.[variantKey]) ?? productColors ?? [];
    const allowedValues = new Set(allowedColors.flatMap((c) => [c.value?.toLowerCase(), c.key?.toLowerCase()]).filter(Boolean));

    const productId = this.dataset?.productId;
    const forms = this.#getAllProductForms();

    let savedColor = null;
    for (const form of forms) {
      const colorInput = form.querySelector('input[name="properties[Text Colour]"]');
      if (colorInput?.value) {
        savedColor = colorInput.value;
        break;
      }
    }
    if (!savedColor && productId && window.currentPersonalisation?.[productId]?.color) {
      savedColor = window.currentPersonalisation[productId].color;
    }
    if (!savedColor) return;

    const isColorValid = allowedValues.has(savedColor.toLowerCase?.() ?? savedColor);
    if (isColorValid) return;

    for (const form of forms) {
      const colorInput = form.querySelector('input[name="properties[Text Colour]"]');
      if (colorInput) colorInput.remove();
    }
    if (productId && window.currentPersonalisation?.[productId]) {
      delete window.currentPersonalisation[productId].color;
      if (window.currentPersonalisation._latest?.color) {
        delete window.currentPersonalisation._latest.color;
      }
    }

    if (typeof window.updatePersonaliseButtonText === 'function') {
      window.updatePersonaliseButtonText(forms[0] || null);
    }

    if (allowedColors.length > 0) {
      this.#showPersonaliseColorRequiredState(productId, forms);
    }
  }

  /**
   * Shows color selection required warning.
   * @private
   */
  #showPersonaliseColorRequiredState(productId, forms) {
    const searchRoots = forms.map((f) => f.closest('product-form-component') || f.closest('#quick-add-modal-content')).filter(Boolean);
    const stickyBar = productId ? document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`) : null;
    const uniqueRoots = [...new Set([...searchRoots, ...(stickyBar ? [stickyBar] : [])])];

    for (const form of forms) {
      form.setAttribute('data-personalisation-color-required', 'true');
    }
    for (const searchRoot of uniqueRoots) {
      const confirmationContainer = searchRoot.querySelector('[data-personalise-confirmation]');
      const checkbox = confirmationContainer?.querySelector('[data-personalise-confirm-checkbox]');
      if (checkbox) checkbox.checked = false;
      if (confirmationContainer) confirmationContainer.style.display = 'none';

      const isStickyBar = searchRoot.tagName === 'STICKY-ADD-TO-CART';
      if (!isStickyBar) {
        let msgEl = searchRoot.querySelector('[data-personalise-color-required-msg]');
        if (!msgEl) {
          msgEl = document.createElement('p');
          msgEl.className = 'personalise-color-required-msg';
          msgEl.setAttribute('data-personalise-color-required-msg', '');
          msgEl.textContent = 'Please again select text colour for this variant';
          msgEl.style.cssText = 'margin: 0.25rem 0 0 0; color: #c00; font-size: 0.875rem;';
          const personaliseBtn = searchRoot.querySelector('[data-personalise-button]');
          if (personaliseBtn && personaliseBtn.nextElementSibling) {
            personaliseBtn.nextElementSibling.before(msgEl);
          } else if (personaliseBtn) {
            personaliseBtn.after(msgEl);
          } else {
            searchRoot.querySelector('.product-form-buttons')?.prepend(msgEl);
          }
        }
        if (msgEl) msgEl.style.display = '';
      } else {
        const msgEl = searchRoot.querySelector('[data-personalise-color-required-msg]');
        if (msgEl) msgEl.style.display = 'none';
      }
    }

    if (typeof window.updateAddToCartButtonState === 'function') {
      window.updateAddToCartButtonState();
    }

    const quickAddModal = document.getElementById('quick-add-modal-content');
    if (quickAddModal?.hasAttribute('data-build-your-set') && typeof window.updateBuildYourSetAddToSetState === 'function') {
      window.updateBuildYourSetAddToSetState(quickAddModal);
    }

    for (const root of uniqueRoots) {
      const addToCartContainers = root.querySelectorAll('add-to-cart-component');
      addToCartContainers.forEach((c) => {
        if (c.refs?.addToCartButton) c.disable();
      });
      const sticky = root.tagName === 'STICKY-ADD-TO-CART' ? root : root.querySelector('sticky-add-to-cart');
      const stickyBtn = sticky?.refs?.addToCartButton || sticky?.querySelector('.sticky-add-to-cart__button');
      if (stickyBtn && sticky?.dataset?.variantAvailable === 'true') {
        stickyBtn.disabled = true;
      }
    }
  }

  /**
   * Rebuilds color grid for selected variant.
   * @private
   */
  #updateColorGridForVariant(variantKey, map) {
    const colorGrid = this.refs?.colorGrid || this.querySelector('.personalise-modal__color-grid');
    if (!colorGrid) return;

    const { variantColors = {}, productColors = [] } = map;
    const colors = (variantKey && variantColors?.[variantKey]) ?? productColors ?? [];

    let savedColor = this.personalisationData?.color || this.selectedColor;
    if (!savedColor) {
      for (const form of this.#getAllProductForms()) {
        const colorInput = form.querySelector('input[name="properties[Text Colour]"]:checked, input[name="properties[Text Colour]"]');
        if (colorInput?.value) {
          savedColor = colorInput.value;
          break;
        }
      }
    }
    if (!savedColor && this.dataset?.productId && window.currentPersonalisation?.[this.dataset.productId]?.color) {
      savedColor = window.currentPersonalisation[this.dataset.productId].color;
    }

    if (!colorGrid.hasAttribute('data-color-click-delegation')) {
      colorGrid.setAttribute('data-color-click-delegation', 'true');
      colorGrid.addEventListener('click', (e) => {
        const btn = e.target.closest('.personalise-modal__color-button');
        if (btn) this.selectColor(e);
      });
    }

    const colorField = colorGrid.closest('.personalise-modal__field');
    if (!colorField) return;

    if (colors.length === 0) {
      colorField.style.display = 'none';
      colorGrid.innerHTML = '';
      this.selectedColor = null;
      this.personalisationData.color = null;
      this.updateSaveButton();
      this.updateCbPreviewOverlay();
      return;
    }

    colorField.style.display = '';

    const fragment = document.createDocumentFragment();
    colors.forEach(({ value, key }) => {
      const label = document.createElement('label');
      label.className = 'personalise-modal__color-button variant-option__button-label variant-option__button-label--image-thumbnail';
      label.dataset.color = key;
      label.title = value;
      label.setAttribute('ref', 'colorButton');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'personalise-color';
      input.value = value;
      label.appendChild(input);

      const thumbWrap = document.createElement('div');
      thumbWrap.className = 'variant-option__image-thumbnail';

      const iconWrap = document.createElement('div');
      iconWrap.className = 'personalise-modal__color-icon';

      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--unscaled';
      swatch.style.cssText = `--swatch-background: ${key}; background-color: ${key};`;
      iconWrap.appendChild(swatch);
      thumbWrap.appendChild(iconWrap);
      label.appendChild(thumbWrap);

      fragment.appendChild(label);
    });

    colorGrid.innerHTML = '';
    colorGrid.appendChild(fragment);

    this.selectedColor = null;
    this.personalisationData.color = null;
    this.updateSaveButton();
    this.updateCbPreviewOverlay();

    this.#attachInputListeners();

    if (savedColor) {
      const allowedValues = new Set(colors.flatMap((c) => [c.value?.toLowerCase(), c.key?.toLowerCase()]).filter(Boolean));
      if (allowedValues.has(String(savedColor).toLowerCase())) {
        this.selectColorByName(savedColor);
        return;
      }
    }

    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (dialog?.open) {
      requestAnimationFrame(() => {
        const firstRadio = colorGrid.querySelector('input[type="radio"]');
        if (firstRadio) {
          firstRadio.checked = true;
          const label = firstRadio.closest('.personalise-modal__color-button');
          if (label) {
            colorGrid.querySelectorAll('.personalise-modal__color-button').forEach((l) => l.classList.remove('personalise-modal__color-button--selected'));
            label.classList.add('personalise-modal__color-button--selected');
          }
          this.selectedColor = firstRadio.value;
          this.personalisationData.color = firstRadio.value;
          this.updateSaveButton();
          this.updateCbPreviewOverlay();
        }
      });
    }
  }

  /**
   * Sets up variant change listener.
   * @private
   */
  #setupCbVariantChangeListener() {
    if (this.dataset.context !== 'product-page') return;
    if (!this.querySelector('script[data-cb-personalization-variants]')) return;

    const handleVariantUpdate = (event) => {
      const productId = event.detail?.data?.productId;
      const ourProductId = this.dataset.productId;
      if (ourProductId && productId && productId !== ourProductId) return;

      const variant = event.detail?.resource;
      const variantId = variant?.id?.toString?.() ?? variant?.id;
      if (!variantId) return;

      this._lastVariantIdFromProductPage = String(variantId);
      this.#updateCbPreviewForVariant(variantId);
    };

    const target = document.body;
    target.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);

    this._cbVariantUpdateHandler = handleVariantUpdate;
    this._cbVariantUpdateTarget = target;
  }

  /**
   * Finds the add-to-cart form.
   * @returns {HTMLFormElement|null}
   */
  #getProductForm() {
    const forms = this.#getAllProductForms();
    return forms[0] || null;
  }

  /**
   * Finds ALL add-to-cart forms for this product.
   * @returns {HTMLFormElement[]}
   */
  #getAllProductForms() {
    const productId = this.dataset?.productId;
    const forms = [];

    const addFormFromComponent = (comp) => {
      const form = comp?.querySelector('form[data-type="add-to-cart-form"]');
      if (form && !forms.includes(form)) forms.push(form);
    };

    const quickAddModal = this.closest('#quick-add-modal-content');
    if (quickAddModal) {
      const formComponent = productId
        ? quickAddModal.querySelector(`product-form-component[data-product-id="${productId}"]`)
        : quickAddModal.querySelector('product-form-component');
      addFormFromComponent(formComponent);
      if (forms.length > 0) return forms;
    }

    if (productId) {
      const section = this.closest('.shopify-section');
      const searchRoot = section || document;
      const formComponent = searchRoot.querySelector(`product-form-component[data-product-id="${productId}"]`);
      addFormFromComponent(formComponent);
    }

    if (productId) {
      document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`).forEach(addFormFromComponent);
    }

    const formComponent = this.closest('product-form-component');
    addFormFromComponent(formComponent);

    if (forms.length === 0) {
      const anyForm = document.querySelector('form[data-type="add-to-cart-form"]');
      if (anyForm) forms.push(anyForm);
    }
    return forms;
  }

  /**
   * Sets up variant color change listener in modal.
   * @private
   */
  #setupVariantColorChangeListener() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog) {
      setTimeout(() => this.#setupVariantColorChangeListener(), 100);
      return;
    }

    const attachVariantColorListeners = () => {
      const variantColorRadios = this.querySelectorAll('.personalise-modal__variant-color-grid input[type="radio"][data-variant-id]');
      variantColorRadios.forEach((radio) => {
        if (radio.dataset.variantColorListener) return;
        radio.dataset.variantColorListener = 'true';
        radio.addEventListener('change', this.#handleVariantColorChange.bind(this));
      });
    };

    attachVariantColorListeners();

    const observer = new MutationObserver(() => attachVariantColorListeners());
    observer.observe(dialog, { childList: true, subtree: true });
  }

  /**
   * Parses product variants map from embedded JSON.
   * @returns {Array}
   */
  #getProductVariantsMap() {
    const script = this.querySelector('script[data-product-variants-map]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Resolves target variant ID when a color swatch is clicked.
   * @param {HTMLInputElement} radio
   * @returns {string|undefined}
   */
  #resolveVariantIdForColorChange(radio) {
    const directVariantId = radio.dataset?.variantId;
    const colorVariantIds = (radio.dataset?.variantIds || '').split(',').map((id) => id.trim()).filter(Boolean);

    if (colorVariantIds.length <= 1) return directVariantId || colorVariantIds[0];

    const currentVariantId = this.#getVariantIdForSync();
    if (!currentVariantId) return directVariantId;

    const allVariants = this.#getProductVariantsMap();
    if (!allVariants.length) return directVariantId;

    const currentVariant = allVariants.find((v) => String(v.id) === String(currentVariantId));
    if (!currentVariant) return directVariantId;

    const selectedColorValue = radio.value;
    const colorVariantIdSet = new Set(colorVariantIds);
    const optionKeys = ['option1', 'option2', 'option3'].filter((k) => currentVariant[k] != null);

    const match = allVariants.find((v) => {
      if (!colorVariantIdSet.has(String(v.id))) return false;
      return optionKeys.every((key) => v[key] === currentVariant[key] || v[key] === selectedColorValue);
    });

    return match ? String(match.id) : directVariantId;
  }

  #handleVariantColorChange(event) {
    const radio = event.target;
    if (!radio?.checked) return;

    const variantId = this.#resolveVariantIdForColorChange(radio);
    if (!variantId) return;

    this.#updateVariantColorSelectedValue(radio);

    const forms = this.#getAllProductForms();
    forms.forEach((form) => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.disabled = false;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.#syncVariantPickersToVariant(variantId);
    this.#updateCbPreviewForVariant(variantId);

    const productId = this.dataset?.productId;
    const productPagePicker = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker');
    const isProductPage = productPagePicker && !productPagePicker.closest('#quick-add-modal-content') && !window.cartPersonalizationContext;
    
    if (isProductPage) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      if (url.href !== window.location.href) {
        history.replaceState({}, '', url.toString());
      }
      const fullUrl = url.toString();
      fetch(fullUrl)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const textContent = doc.querySelector(`variant-picker[data-product-id="${productId}"] script[type="application/json"]`)?.textContent
            || doc.querySelector('variant-picker script[type="application/json"]')?.textContent;
          const variantResource = textContent ? (() => {
            try {
              return JSON.parse(textContent);
            } catch {
              return { id: Number(variantId) };
            }
          })() : { id: Number(variantId) };

          const picker = productId
            ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
            : document.querySelector('variant-picker');
          if (picker && !picker.closest('#quick-add-modal-content')) {
            const newPicker = doc.querySelector(`variant-picker[data-product-id="${productId}"]`) || doc.querySelector('variant-picker');
            if (newPicker) {
              morph(picker, newPicker);
            }
            picker.dispatchEvent(new VariantUpdateEvent(variantResource, this.id || 'personalise-dialog', {
              productId: productId || '',
              html: doc
            }));
          }
        })
        .catch(() => {
          const picker = productId
            ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
            : document.querySelector('variant-picker');
          if (picker && !picker.closest('#quick-add-modal-content')) {
            picker.dispatchEvent(new VariantUpdateEvent({ id: Number(variantId) }, this.id || 'personalise-dialog', {
              productId: productId || '',
              html: null
            }));
          }
        });
    }

    const isQuickAddContext = this.hasAttribute('data-quick-add');
    const quickAddModal = isQuickAddContext ? document.getElementById('quick-add-modal-content') : null;
    if (quickAddModal) {
      const quickAddPicker = productId
        ? quickAddModal.querySelector(`variant-picker[data-product-id="${productId}"]`)
        : quickAddModal.querySelector('variant-picker');
      const productFormComponent = quickAddModal.querySelector('product-form-component');
      const productUrl = quickAddPicker?.dataset?.productUrl || productFormComponent?.dataset?.productUrl;
      if (productUrl) {
        const fetchUrl = productUrl.includes('?')
          ? `${productUrl.split('?')[0]}?variant=${variantId}`
          : `${productUrl}?variant=${variantId}`;
        const fullUrl = fetchUrl.startsWith('/')
          ? new URL(fetchUrl, window.location.origin).toString()
          : fetchUrl;
        fetch(fullUrl)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newMediaGallery = doc.querySelector('media-gallery');
            const newFirstImage =
              newMediaGallery?.querySelector(
                '.product-media img, slideshow-slide img, .product-media-container img, img[src], img[data-src]'
              ) || doc.querySelector('.product-information__media img, media-gallery img');
            const findCurrentImage = () =>
              quickAddModal.querySelector(
                '.product-information__media slideshow-slide:first-child img, .product-information__media .product-media img, .product-information__media .product-media-container img, .product-information__media img, media-gallery img'
              );
            const currentFirstImage = findCurrentImage();
            if (newFirstImage && currentFirstImage) {
              const newImageSrc =
                newFirstImage.getAttribute('src') ||
                newFirstImage.src ||
                newFirstImage.getAttribute('data-src') ||
                newFirstImage.getAttribute('srcset')?.trim().split(/\s+/)[0];
              if (newImageSrc && currentFirstImage.src !== newImageSrc) {
                currentFirstImage.style.transition = 'none';
                currentFirstImage.style.opacity = '1';
                currentFirstImage.src = newImageSrc;
                if (newFirstImage.getAttribute('srcset')) {
                  currentFirstImage.srcset = newFirstImage.getAttribute('srcset');
                }
                if (newFirstImage.getAttribute('sizes')) {
                  currentFirstImage.sizes = newFirstImage.getAttribute('sizes');
                }
                if (newFirstImage.getAttribute('alt')) {
                  currentFirstImage.alt = newFirstImage.getAttribute('alt');
                }
              }
            }
          })
          .catch(() => {});
      }
    }

    const variantResource = { id: Number(variantId) };
    const section = this.closest('.shopify-section') || document.body;
    section.dispatchEvent(new VariantUpdateEvent(variantResource, this.id || 'personalise-dialog', {
      productId: productId || '',
      html: null
    }));

    this.updateSaveButton();
  }

  /**
   * Syncs variant color radio selection.
   * @private
   */
  #syncVariantColorSelection(variantId) {
    if (!variantId) return;
    const variantIdStr = String(variantId);
    const radios = this.querySelectorAll('.personalise-modal__variant-color-grid input[type="radio"][data-variant-id]');
    let checkedRadio = null;
    radios.forEach((radio) => {
      const matchesId = radio.dataset.variantId === variantIdStr;
      const variantIds = radio.dataset.variantIds || '';
      const matchesIds = variantIds.split(',').map((id) => id.trim()).includes(variantIdStr);
      radio.checked = matchesId || matchesIds;
      if (radio.checked) checkedRadio = radio;
    });
    this.#updateVariantColorSelectedValue(checkedRadio);
  }

  /**
   * Syncs variant pickers UI.
   * @private
   */
  #syncVariantPickersToVariant(variantId) {
    if (!variantId) return;
    const variantIdStr = String(variantId);
    const productId = this.dataset?.productId;
    const variantPickers = productId
      ? document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelectorAll('variant-picker');
    variantPickers.forEach((picker) => {
      const inputs = picker.querySelectorAll('input[type="radio"][data-variant-id], input[type="radio"][data-variant-ids]');
      let matchingInput = null;
      for (const input of inputs) {
        const matchesId = input.dataset.variantId === variantIdStr;
        const variantIds = (input.dataset.variantIds || '').split(',').map((id) => id.trim());
        const matchesIds = variantIds.includes(variantIdStr);
        if (matchesId || matchesIds) {
          matchingInput = input;
          break;
        }
      }
      if (matchingInput) {
        picker.updateSelectedOption?.(matchingInput);
        const fieldset = matchingInput.closest('fieldset');
        const valueSpan = fieldset?.querySelector('.variant-option__selected-value');
        if (valueSpan) {
          valueSpan.textContent = matchingInput.value || '';
        }
      }
    });
  }

  /**
   * Updates variant color selected value label.
   * @private
   */
  #updateVariantColorSelectedValue(checkedRadio) {
    const span = this.querySelector('[data-variant-color-selected-value]');
    if (!span) return;
    span.textContent = checkedRadio?.value ?? '';
  }

  /**
   * Gets current variant ID for sync.
   * @returns {string|null}
   * @private
   */
  #getVariantIdForSync() {
    const cartContext = window.cartPersonalizationContext;
    if (cartContext?.variantId) {
      return String(cartContext.variantId);
    }
    if (this.closest('#quick-add-modal-content')) {
      const productForm = this.#getProductForm();
      const variantInput = productForm?.querySelector('input[name="id"]');
      if (variantInput?.value) return String(variantInput.value);
      if (this._openingVariantId) {
        const id = this._openingVariantId;
        this._openingVariantId = null;
        return id;
      }
      return null;
    }
    if (this._lastVariantIdFromProductPage) {
      return this._lastVariantIdFromProductPage;
    }
    const productForm = this.#getProductForm();
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput?.value) {
        return String(variantInput.value);
      }
    }
    if (this._openingVariantId) {
      const id = this._openingVariantId;
      this._openingVariantId = null;
      return id;
    }
    return null;
  }

  /**
   * Syncs variant color and dynamic preview on modal open.
   * @private
   */
  #syncVariantAndPreviewOnOpen() {
    const variantId = this.#getVariantIdForSync();
    if (variantId) {
      this.#updateCbPreviewForVariant(variantId);
      this.#syncVariantColorSelection(variantId);
    }
  }

  /**
   * Sets up input listeners to update save button state.
   * @private
   */
  #setupInputChangeListeners() {
    const observer = new MutationObserver(() => {
      this.#attachInputListeners();
    });
    
    const checkDialog = () => {
      const dialog = this.refs?.dialog || this.querySelector('dialog');
      if (dialog) {
        observer.observe(dialog, { childList: true, subtree: true });
        this.#attachInputListeners();
      } else {
        setTimeout(checkDialog, 100);
      }
    };
    
    setTimeout(checkDialog, 100);
  }
  
  /**
   * Attaches event listeners to inputs for validation.
   * @private
   */
  #attachInputListeners() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog) return;
    
    const colorRadios = dialog.querySelectorAll('.personalise-modal__color-grid input[type="radio"]');
    colorRadios.forEach(radio => {
      radio.removeEventListener('change', this.#handleColorRadioChange);
      radio.addEventListener('change', this.#handleColorRadioChange);
    });

    const designRadios = dialog.querySelectorAll('.personalise-modal__design-grid input[type="radio"]');
    designRadios.forEach(radio => {
      radio.removeEventListener('change', this.#handleDesignRadioChange);
      radio.addEventListener('change', this.#handleDesignRadioChange);
    });
    
    const allInputs = dialog.querySelectorAll('input:not([type="radio"]):not([type="checkbox"]), textarea, select');
    allInputs.forEach(input => {
      if (input.dataset.hasSaveButtonListener) return;
      
      input.dataset.hasSaveButtonListener = 'true';
      input.addEventListener('input', () => {
        this.updateSaveButton();
        if (
          input.name === 'properties[Date of Birth]' ||
          input.id === 'dob_field_val' ||
          input.name === 'properties[Time]' ||
          input.name === 'properties[Weight]'
        ) {
          this.#updateCbOptionalFieldPreviewOverlays();
        } else {
          this.updateCbPreviewOverlay();
        }
      });
      input.addEventListener('change', () => {
        this.updateSaveButton();
        if (
          input.name === 'properties[Date of Birth]' ||
          input.id === 'dob_field_val' ||
          input.name === 'properties[Time]' ||
          input.name === 'properties[Weight]'
        ) {
          this.#updateCbOptionalFieldPreviewOverlays();
        } else {
          this.updateCbPreviewOverlay();
        }
      });
    });
    
    const checkboxes = dialog.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (checkbox.dataset.hasSaveButtonListener) return;
      checkbox.dataset.hasSaveButtonListener = 'true';
      checkbox.addEventListener('change', () => this.updateSaveButton());
    });
  }
  
  /**
   * Handles color radio change events.
   * @private
   */
  #handleColorRadioChange = (event) => {
    const radio = event.target;
    if (radio.checked) {
      const colorButton = radio.closest('.personalise-modal__color-button');
      if (colorButton) {
        const colorName = colorButton.dataset.color || radio.value;
        this.selectedColor = colorName;
        this.personalisationData.color = colorName;
      }
    }
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  };

  /**
   * Handles design radio change events.
   * @private
   */
  #handleDesignRadioChange = (event) => {
    const radio = event.target;
    if (radio.checked) {
      const designButton = radio.closest('.personalise-modal__design-button');
      if (designButton) {
        const designName = designButton.dataset.design || radio.dataset.designName || radio.value;
        this.selectDesignByName(designName);
      }
    }
    this.updateSaveButton();
  };

  /**
   * Sets up click handlers for close buttons.
   * @private
   */
  #setupCloseButtonHandlers() {
    const component = this;
    const dialogElement = this.refs?.dialog || this.querySelector('dialog');
    
    if (!dialogElement) {
      setTimeout(() => this.#setupCloseButtonHandlers(), 50);
      return;
    }
    
    const handleClose = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      event.personaliseDialogClick = true;
      event.cancelBubble = true;
      event.returnValue = false;
      
      component.closePersonaliseOnly();
      return false;
    };
    
    const selectors = [
      '[data-personalise-close="true"]',
      '.personalise-modal__close',
      '.personalise-modal__cancel-button'
    ];
    
    selectors.forEach(selector => {
      const buttons = this.querySelectorAll(selector);
      buttons.forEach(btn => {
        btn.removeEventListener('click', handleClose, { capture: true });
        btn.removeEventListener('mousedown', handleClose, { capture: true });
        
        const wrappedMousedown = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.personaliseDialogClick = true;
          handleClose(e);
          return false;
        };
        
        const wrappedClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.personaliseDialogClick = true;
          handleClose(e);
          return false;
        };
        
        btn.addEventListener('mousedown', wrappedMousedown, { capture: true, passive: false });
        btn.addEventListener('click', wrappedClick, { capture: true, passive: false });
      });
    });
    
    if (!dialogElement.dataset.personaliseCloseHandlerSetup) {
      const handleDelegatedClick = (event) => {
        const closeBtn = event.target.closest('[data-personalise-close="true"], .personalise-modal__close, .personalise-modal__cancel-button');
        if (closeBtn) {
          handleClose(event);
        }
      };
      
      dialogElement.addEventListener('click', handleDelegatedClick, { capture: true, passive: false });
      dialogElement.dataset.personaliseCloseHandlerSetup = 'true';
    }
  }
  
  /**
   * Sets form attribute on Baby/Kid/Mum inputs.
   */
  #setFormAttributeOnInputs() {
    let productForm = null;
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    if (!productForm) {
      productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (productForm && productForm.id) {
      const inputs = this.querySelectorAll('[data-form-id]');
      inputs.forEach(input => {
        input.setAttribute('form', productForm.id);
      });
    }
  }
  
  /**
   * Sets up form submit listener for current session.
   */
  setupFormSubmitListenerForCurrentSession() {
    let productForm = null;
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    if (!productForm) {
      productForm = document.querySelector(`form[data-type="add-to-cart-form"]`);
    }
    if (productForm) {
      this.setupFormSubmitListener(productForm);
    }
  }

  /**
   * Returns supported personalisation fields based on DOM presence.
   * @returns {Object}
   */
  #getSupportedPersonalisationFields() {
    const root = this;
    return {
      name: !!(root.querySelector('#personalise-name') || root.querySelector('input[name="personalise-name"]')),
      font: !!(root.querySelector('.personalise-modal__font-grid') || root.refs?.fontGrid),
      color: !!(root.querySelector('.personalise-modal__color-grid') || root.refs?.colorGrid),
      design: !!(root.querySelector('.personalise-modal__design-grid') || root.querySelector('#personalise-design') || root.refs?.designInput),
      dob: !!(root.querySelector('input[name="properties[Date of Birth]"]') || root.refs?.dobInput),
      schoolYear: !!(root.querySelector('input[name="properties[School Year]"]') || root.refs?.schoolYearInput),
      name1: !!(root.querySelector('input[name="properties[Name 1]"]') || root.refs?.name1Input),
      name2: !!(root.querySelector('input[name="properties[Name 2]"]') || root.refs?.name2Input),
      name3: !!(root.querySelector('input[name="properties[Name 3]"]') || root.refs?.name3Input),
      name4: !!(root.querySelector('input[name="properties[Name 4]"]') || root.refs?.name4Input),
      textbox: !!(root.querySelector('[name="properties[Personalisation:]"]') || root.refs?.textboxInput),
      message: !!(root.querySelector('textarea[name="properties[Message]"]') || root.refs?.messageInput),
      optionalDob: !!(root.querySelector('#dob_field_val') || root.querySelector('input[id="dob_field_val"]') || root.refs?.optionalDobInput),
      time: !!(root.querySelector('input[name="properties[Time]"]') || root.refs?.timeInput),
      weight: !!(root.querySelector('input[name="properties[Weight]"]') || root.refs?.weightInput),
      babyName: !!(root.querySelector('[data-name-input="baby"]') || root.refs?.babyNameInput),
      kidName: !!(root.querySelector('[data-name-input="kid"]') || root.refs?.kidNameInput),
      mumName: !!(root.querySelector('[data-name-input="mum"]') || root.refs?.mumNameInput)
    };
  }

  /**
   * Filters personalisation object to supported fields.
   * @param {Object} data
   * @returns {Object}
   */
  #filterToSupportedFields(data) {
    if (!data) return {};
    const supported = this.#getSupportedPersonalisationFields();
    const filtered = {};
    for (const key of Object.keys(data)) {
      if (supported[key] && data[key] != null && (!(typeof data[key] === 'string') || data[key].trim() !== '')) {
        filtered[key] = data[key];
      }
    }
    return filtered;
  }

  /**
   * Loads saved personalisation from form inputs or context.
   */
  loadSavedPersonalisation() {
    if (window.cartPersonalizationContext && window.cartPersonalizationContext.properties) {
      const properties = window.cartPersonalizationContext.properties;
      const personalisation = {
        name: properties['Name'] || null,
        name1: properties['Name 1'] || null,
        name2: properties['Name 2'] || null,
        name3: properties['Name 3'] || null,
        name4: properties['Name 4'] || null,
        babyName: properties['Baby\'s Name'] || null,
        kidName: properties['Kid\'s Name'] || null,
        mumName: properties['Mum\'s Name'] || null,
        dob: properties['Date of Birth'] || null,
        optionalDob: properties['Personalise Date of Birth'] || null,
        schoolYear: properties['School Year'] || null,
        color: properties['Text Colour'] || null,
        font: properties['Text Font'] || null,
        design: properties['Design'] || null,
        textbox: properties['Personalisation:'] || null,
        message: properties['Message'] || null,
        time: properties['Time'] || null,
        weight: properties['Weight'] || null
      };
      
      const filtered = this.#filterToSupportedFields(personalisation);
      this.personalisationData = {
        ...this.personalisationData,
        ...filtered
      };
      
      if (Object.keys(filtered).length > 0) {
        return;
      }
    }
    
    let form = null;
    const forms = this.#getAllProductForms();
    if (forms.length > 0) {
      form = forms[0];
    }
    if (!form) {
      form = document.querySelector('form[data-type="add-to-cart-form"]');
    }
    if (!form) return;
    
    const personalisation = {};
    
    const nameInput = form.querySelector('input[name="properties[Name]"]');
    if (nameInput && nameInput.value.trim()) {
      personalisation.name = nameInput.value.trim();
    }
    
    const fontInput = form.querySelector('input[name="properties[Text Font]"]:checked') || 
                      form.querySelector('input[name="properties[Text Font]"]');
    if (fontInput && fontInput.value.trim()) {
      personalisation.font = fontInput.value.trim();
    }
    
    const colorInput = form.querySelector('input[name="properties[Text Colour]"]:checked') || 
                       form.querySelector('input[name="properties[Text Colour]"]');
    if (colorInput && colorInput.value.trim()) {
      personalisation.color = colorInput.value.trim();
    }

    const designInput = form.querySelector('input[name="properties[Design]"]') || form.querySelector('#personalise-design');
    if (designInput && designInput.value.trim()) {
      personalisation.design = designInput.value.trim();
    }
    
    const dobInput = form.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && dobInput.value.trim()) {
      personalisation.dob = this.#dateInputValueToDobProperty(dobInput.value.trim());
    }
    
    const schoolYearInput = form.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput && schoolYearInput.value.trim()) {
      personalisation.schoolYear = schoolYearInput.value.trim();
    }
    
    const name1Input = form.querySelector('input[name="properties[Name 1]"]');
    if (name1Input && name1Input.value.trim()) {
      personalisation.name1 = name1Input.value.trim();
    }
    
    const name2Input = form.querySelector('input[name="properties[Name 2]"]');
    if (name2Input && name2Input.value.trim()) {
      personalisation.name2 = name2Input.value.trim();
    }
    
    const name3Input = form.querySelector('input[name="properties[Name 3]"]');
    if (name3Input && name3Input.value.trim()) {
      personalisation.name3 = name3Input.value.trim();
    }
    
    const name4Input = form.querySelector('input[name="properties[Name 4]"]');
    if (name4Input && name4Input.value.trim()) {
      personalisation.name4 = name4Input.value.trim();
    }
    
    const textboxInput = form.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput && textboxInput.value.trim()) {
      personalisation.textbox = textboxInput.value.trim();
    }
    
    const messageInput = form.querySelector('textarea[name="properties[Message]"]');
    if (messageInput && messageInput.value.trim()) {
      personalisation.message = messageInput.value.trim();
    }
    
    let optionalDobInput = form.querySelector('input[name="properties[Personalise Date of Birth]"]');
    if (!optionalDobInput) {
      optionalDobInput = form.querySelector('#dob_field_val');
    }
    if (optionalDobInput && optionalDobInput.value.trim()) {
      const raw = optionalDobInput.value.trim();
      personalisation.optionalDob = optionalDobInput.type === 'date' || /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? this.#dateInputValueToDobProperty(raw)
        : this.#normalizeDobPropertyString(raw);
    }
    
    const timeInput = form.querySelector('input[name="properties[Time]"]');
    if (timeInput && timeInput.value.trim()) {
      personalisation.time = timeInput.value.trim();
    }
    
    const weightInput = form.querySelector('input[name="properties[Weight]"]');
    if (weightInput && weightInput.value.trim()) {
      personalisation.weight = weightInput.value.trim();
    }
    
    const babyNameInput = form.querySelector('input[name="properties[Baby\'s Name]"]');
    if (babyNameInput && babyNameInput.value.trim()) {
      personalisation.babyName = babyNameInput.value.trim();
    }
    
    const kidNameInput = form.querySelector('input[name="properties[Kid\'s Name]"]');
    if (kidNameInput && kidNameInput.value.trim()) {
      personalisation.kidName = kidNameInput.value.trim();
    }
    
    const mumNameInput = form.querySelector('input[name="properties[Mum\'s Name]"]');
    if (mumNameInput && mumNameInput.value.trim()) {
      personalisation.mumName = mumNameInput.value.trim();
    }
    
    this.personalisationData = {
      ...this.personalisationData,
      ...personalisation
    };
    
    if (Object.keys(personalisation).length === 0) {
      let productId = null;
      const productFormComponent = form?.closest('product-form-component');
      if (productFormComponent) {
        productId = productFormComponent.dataset?.productId;
      }
      
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (!productId && quickAddModal) {
        const quickAddFormComponent = quickAddModal.querySelector('product-form-component');
        productId = quickAddFormComponent?.dataset?.productId;
      }
      
      if (productId && window.currentPersonalisation) {
        let savedPersonalisation = null;
        if (window.currentPersonalisation[productId]) {
          savedPersonalisation = { ...window.currentPersonalisation[productId] };
        } else if (window.currentPersonalisation._latest) {
          savedPersonalisation = { ...window.currentPersonalisation._latest };
        } else if (window.currentPersonalisation && typeof window.currentPersonalisation === 'object' && !Array.isArray(window.currentPersonalisation)) {
          const hasProductKeys = Object.keys(window.currentPersonalisation).some(key => key.startsWith('_') || /^\d+$/.test(key));
          if (!hasProductKeys) {
            savedPersonalisation = { ...window.currentPersonalisation };
          }
        }
        
        if (savedPersonalisation) {
          Object.keys(savedPersonalisation).forEach(key => {
            if (key.startsWith('_') || 
                !savedPersonalisation[key] || 
                (typeof savedPersonalisation[key] === 'string' && !savedPersonalisation[key].trim())) {
              delete savedPersonalisation[key];
            }
          });
          const filtered = this.#filterToSupportedFields(savedPersonalisation);
          if (Object.keys(filtered).length > 0) {
            this.personalisationData = {
              ...this.personalisationData,
              ...filtered
            };
          }
        }
      }
    }
  }

  /**
   * Shows the dialog.
   * @param {string} [variantIdFromButton]
   */
  showDialog(variantIdFromButton) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (variantIdFromButton) {
      this._openingVariantId = String(variantIdFromButton);
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    this.#setupCloseButtonHandlers();

    if (this.dataset.fullScreenPersonalisation === 'true' && !this.hasAttribute('data-quick-add') && !window.cartPersonalizationContext) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('customize') !== 'true') {
        url.searchParams.set('customize', 'true');
        history.replaceState({}, '', url.toString());
        this._weAddedCustomizeParam = true;
      }
    }

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      
      this.#initializeSaveButtonState();

      if (this.dataset.cbPersonalizationImage) {
        if (!this._cbPreviewResizeHandler) {
          this._cbPreviewResizeHandler = () => {
            this.updateCbPreviewOverlay();
          };
        }
        window.addEventListener('resize', this._cbPreviewResizeHandler);
      }
      if (
        this.dataset.cbDobPersonalizationPosition ||
        this.dataset.cbTobPersonalizationPosition ||
        this.dataset.cbWeightPersonalizationPosition
      ) {
        this.#updateCbOptionalFieldPreviewOverlays();
      }

      this.addEventListener('keydown', this.#handleKeyDownPersonalise);
      
      requestAnimationFrame(() => {
        this.#setupCloseButtonHandlers();
        setTimeout(() => {
          this.#setupCloseButtonHandlers();
          setTimeout(() => {
            this.#setupCloseButtonHandlers();
            setTimeout(() => {
              this.#setupCloseButtonHandlers();
            }, 200);
          }, 200);
        }, 100);
      });
    });
    
    const quickAddModal = document.getElementById('quick-add-modal-content');
    const isBuildYourSet = quickAddModal?.hasAttribute('data-build-your-set');
    const isCartContext = window.cartPersonalizationContext && window.cartPersonalizationContext.properties;
    const isQuickAddContext = quickAddModal && !isBuildYourSet;
    
    const currentProductForm = quickAddModal?.querySelector('form[data-type="add-to-cart-form"]');
    const currentProductId = currentProductForm?.closest('product-form-component')?.dataset?.productId;
    const previousProductId = this._lastProductId;
    
    const shouldClearState = (isBuildYourSet && !isCartContext) || 
                             (isQuickAddContext && !isCartContext && (currentProductId !== previousProductId || !previousProductId));
    
    if (shouldClearState) {
      this.selectedFont = null;
      this.selectedColor = null;
      this.personalisationData = {
        name: '',
        font: null,
        color: null,
        design: null,
        dob: null,
        schoolYear: null,
        name1: null,
        name2: null,
        name3: null,
        name4: null,
        textbox: null,
        message: null,
        optionalDob: null,
        time: null,
        weight: null,
        babyName: null,
        kidName: null,
        mumName: null
      };
      
      requestAnimationFrame(() => {
        const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
        if (nameInput) {
          nameInput.value = '';
          const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
          if (charCounter) {
            charCounter.textContent = '0';
          }
        }
        
        if (this.refs.fontGrid) {
          const fontButtons = this.refs.fontGrid.querySelectorAll('.personalise-modal__font-button');
          fontButtons.forEach(btn => {
            btn.classList.remove('personalise-modal__font-button--selected');
          });
        }
        
        const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
        if (colorGrid) {
          const colorRadios = colorGrid.querySelectorAll('input[type="radio"]');
          colorRadios.forEach(radio => {
            radio.checked = false;
          });
        }

        const designGrid = this.refs.designGrid || this.querySelector('.personalise-modal__design-grid');
        if (designGrid) {
          const designRadios = designGrid.querySelectorAll('input[type="radio"]');
          designRadios.forEach(radio => {
            radio.checked = false;
          });
          designGrid.querySelectorAll('.personalise-modal__design-button').forEach(btn => btn.classList.remove('personalise-modal__design-button--selected'));
        }

        const designInput = this.refs.designInput || this.querySelector('#personalise-design');
        if (designInput) {
          designInput.value = '';
          const statusEl = this.refs.designMatchStatus || this.querySelector('[data-design-status]');
          if (statusEl) statusEl.textContent = '';
          const overlay = this.refs.previewDesignOverlay || this.querySelector('.personalise-modal__preview-design-overlay');
          if (overlay) {
            overlay.style.display = 'none';
            overlay.removeAttribute('src');
          }
        }
        
        const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
        if (babyNameInput) {
          babyNameInput.value = '';
          this.updateNameTabCharCounter('baby', 0);
          this.updateNameTabIcon('baby', false);
        }
        
        const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
        if (kidNameInput) {
          kidNameInput.value = '';
          this.updateNameTabCharCounter('kid', 0);
          this.updateNameTabIcon('kid', false);
        }
        
        const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
        if (mumNameInput) {
          mumNameInput.value = '';
          this.updateNameTabCharCounter('mum', 0);
          this.updateNameTabIcon('mum', false);
        }
        
        const allInputs = this.querySelectorAll('input[type="text"], textarea, input[type="date"]');
        allInputs.forEach(input => {
          if (input.type !== 'radio' && input.type !== 'checkbox') {
            input.value = '';
          }
        });
      });
    }
    
    if (currentProductId) {
      this._lastProductId = currentProductId;
    }
    
    try {
      this.loadSavedPersonalisation();
      
      if (this.personalisationData && Object.keys(this.personalisationData).length > 0) {
        const form = quickAddModal?.querySelector('form[data-type="add-to-cart-form"]') ||
                    this.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]') ||
                    document.querySelector('form[data-type="add-to-cart-form"]');
        
        if (form) {
          const hasNameInput = form.querySelector('input[name="properties[Name]"]');
          const hasFontInput = form.querySelector('input[name="properties[Text Font]"]');
          const hasColorInput = form.querySelector('input[name="properties[Text Colour]"]');
          const hasDesignInput = form.querySelector('input[name="properties[Design]"]');
          
          if ((this.personalisationData.name && !hasNameInput) ||
              (this.personalisationData.font && !hasFontInput) ||
              (this.personalisationData.color && !hasColorInput) ||
              (this.personalisationData.design && !hasDesignInput)) {
            this.updateFormFields(this.personalisationData);
          }
        }
      }
    } catch (error) {
      console.error('Error loading personalisation:', error);
    }
    
    let attempts = 0;
    const maxAttempts = 20;
    
    const checkAndPopulate = () => {
      attempts++;
      const dialog = this.refs.dialog;
      
      if (dialog && dialog.open) {
        requestAnimationFrame(() => {
          try {
            this.#syncVariantAndPreviewOnOpen();
            this.#populateFieldsFromSavedData();
            this.#attachInputListeners();
            this.#syncVariantAndPreviewOnOpen();
            this.#selectFirstColorAndFontIfNotSelected();
            this.updateCbPreviewOverlay();
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this.#selectFirstColorAndFontIfNotSelected();
            }, 150);
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this.#selectFirstColorAndFontIfNotSelected();
            }, 350);
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this._openingVariantId = null;
              this.#selectFirstColorAndFontIfNotSelected();
            }, 550);
          } catch (error) {
            console.error('Error populating fields:', error);
          }
        });
      } else if (attempts < maxAttempts) {
        setTimeout(checkAndPopulate, 50);
      } else {
        try {
          this.#populateFieldsFromSavedData();
          this.#attachInputListeners();
          this.#syncVariantAndPreviewOnOpen();
          this.#selectFirstColorAndFontIfNotSelected();
          this.updateCbPreviewOverlay();
        } catch (error) {
          console.error('Error populating fields after max attempts:', error);
        }
      }
    };
    
    setTimeout(checkAndPopulate, 100);
  }

  /**
   * Handle Escape key for personalise dialog
   * @private
   */
  #handleKeyDownPersonalise = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.closePersonaliseDialog();
  };

  /**
   * Closes personalise modal.
   */
  closePersonaliseOnly = async () => {
    this._openingVariantId = null;
    if (this._cbPreviewResizeHandler) {
      window.removeEventListener('resize', this._cbPreviewResizeHandler);
    }

    this.selectedFont = null;
    this.selectedColor = null;
    this.personalisationData = {
      name: '',
      font: null,
      color: null,
      design: null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: null,
      kidName: null,
      mumName: null
    };
    
    const dialogElement = this.querySelector('dialog') || this.refs?.dialog;

    if (!dialogElement) {
      const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
      const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
      if (!quickAddIsOpen) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }
      return;
    }
    
    if (!dialogElement.open) {
      const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
      const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
      if (!quickAddIsOpen) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }
      return;
    }

    try {
      this.removeEventListener('keydown', this.#handleKeyDownPersonalise);
    } catch (e) {
      // Ignore
    }

    dialogElement.classList.add('dialog-closing');

    try {
      await onAnimationEnd(dialogElement, undefined, {
        subtree: false,
      });
    } catch (e) {
      console.warn('Animation end error:', e);
    }

    try {
      dialogElement.close();
    } catch (e) {
      console.error('Error calling dialog.close():', e);
    }
    
    dialogElement.classList.remove('dialog-closing');

    const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
    const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
    
    if (!quickAddIsOpen) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    if (this.dataset.context !== 'cart-drawer' && !this.hasAttribute('data-quick-add')) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('customize') === 'true') {
        url.searchParams.delete('customize');
        history.replaceState({}, '', url.toString());
      }
    }
    this._weAddedCustomizeParam = false;

    try {
      this.dispatchEvent(new DialogCloseEvent());
    } catch (e) {
      console.error('Error dispatching event:', e);
    }
  };

  handleCloseButton = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    await this.closePersonaliseOnly();
  };

  closePersonaliseDialog = async () => {
    return this.closePersonaliseOnly();
  };

  closeDialog = async () => {
    return this.closePersonaliseOnly();
  };
  
  /**
   * Populates form fields from saved data.
   * @private
   */
  #populateFieldsFromSavedData() {
    if (!this.personalisationData) return;
    
    const hasAnyData = 
      this.personalisationData.name ||
      this.personalisationData.font ||
      this.personalisationData.color ||
      this.personalisationData.design ||
      this.personalisationData.dob ||
      this.personalisationData.optionalDob ||
      this.personalisationData.schoolYear ||
      this.personalisationData.name1 ||
      this.personalisationData.name2 ||
      this.personalisationData.name3 ||
      this.personalisationData.name4 ||
      this.personalisationData.textbox ||
      this.personalisationData.message ||
      this.personalisationData.time ||
      this.personalisationData.weight ||
      this.personalisationData.babyName ||
      this.personalisationData.kidName ||
      this.personalisationData.mumName;
    
    if (!hasAnyData) return;
    
    const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
    if (nameInput && this.personalisationData.name) {
      nameInput.value = this.personalisationData.name;
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
      const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
      if (charCounter) {
        charCounter.textContent = this.personalisationData.name.length;
      }
    }
    
    if (this.personalisationData.font) {
      setTimeout(() => {
        this.selectFontByName(this.personalisationData.font);
      }, 100);
    }
    
    const colorToApply = this.personalisationData.color;
    if (colorToApply) {
      this.selectColorByName(colorToApply);
      setTimeout(() => this.selectColorByName(colorToApply), 100);
      setTimeout(() => this.selectColorByName(colorToApply), 350);
    }

    if (this.personalisationData.design) {
      this.selectDesignByName(this.personalisationData.design);
      setTimeout(() => this.selectDesignByName(this.personalisationData.design), 100);
    }
    
    const dobInput = this.refs.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && this.personalisationData.dob) {
      const forDateInput = this.#dobPropertyToDateInputValue(this.personalisationData.dob);
      if (forDateInput) dobInput.value = forDateInput;
    }
    
    const schoolYearInput = this.refs.schoolYearInput || this.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput && this.personalisationData.schoolYear) schoolYearInput.value = this.personalisationData.schoolYear;
    
    const name1Input = this.refs.name1Input || this.querySelector('input[name="properties[Name 1]"]');
    if (name1Input && this.personalisationData.name1) name1Input.value = this.personalisationData.name1;
    
    const name2Input = this.refs.name2Input || this.querySelector('input[name="properties[Name 2]"]');
    if (name2Input && this.personalisationData.name2) name2Input.value = this.personalisationData.name2;
    
    const name3Input = this.refs.name3Input || this.querySelector('input[name="properties[Name 3]"]');
    if (name3Input && this.personalisationData.name3) name3Input.value = this.personalisationData.name3;
    
    const name4Input = this.refs.name4Input || this.querySelector('input[name="properties[Name 4]"]');
    if (name4Input && this.personalisationData.name4) name4Input.value = this.personalisationData.name4;
    
    const textboxInput = this.refs.textboxInput || this.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput && this.personalisationData.textbox) textboxInput.value = this.personalisationData.textbox;
    
    const messageInput = this.refs.messageInput || this.querySelector('textarea[name="properties[Message]"]');
    if (messageInput && this.personalisationData.message) messageInput.value = this.personalisationData.message;
    
    const optionalDobInput = this.refs.optionalDobInput || this.querySelector('#dob_field_val');
    if (optionalDobInput && this.personalisationData.optionalDob) {
      const forDateInput = this.#dobPropertyToDateInputValue(this.personalisationData.optionalDob);
      if (forDateInput) optionalDobInput.value = forDateInput;
    }
    
    const timeInput = this.refs.timeInput || this.querySelector('input[name="properties[Time]"]');
    if (timeInput && this.personalisationData.time) timeInput.value = this.personalisationData.time;
    
    const weightInput = this.refs.weightInput || this.querySelector('input[name="properties[Weight]"]');
    if (weightInput && this.personalisationData.weight) weightInput.value = this.personalisationData.weight;
    
    const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
    if (babyNameInput && this.personalisationData.babyName) {
      babyNameInput.value = this.personalisationData.babyName;
      this.updateNameTabCharCounter('baby', babyNameInput.value.length);
      let attempts = 0;
      const maxAttempts = 5;
      const updateBabyIcon = () => {
        attempts++;
        const hasValue = babyNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="baby"]`);
        if (icon) {
          this.updateNameTabIcon('baby', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateBabyIcon, 100);
        }
      };
      setTimeout(updateBabyIcon, 100);
    }
    
    const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
    if (kidNameInput && this.personalisationData.kidName) {
      kidNameInput.value = this.personalisationData.kidName;
      this.updateNameTabCharCounter('kid', kidNameInput.value.length);
      let attempts = 0;
      const maxAttempts = 5;
      const updateKidIcon = () => {
        attempts++;
        const hasValue = kidNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="kid"]`);
        if (icon) {
          this.updateNameTabIcon('kid', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateKidIcon, 100);
        }
      };
      setTimeout(updateKidIcon, 100);
    }
    
    const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
    if (mumNameInput && this.personalisationData.mumName) {
      mumNameInput.value = this.personalisationData.mumName;
      this.updateNameTabCharCounter('mum', mumNameInput.value.length);
      let attempts = 0;
      const maxAttempts = 5;
      const updateMumIcon = () => {
        attempts++;
        const hasValue = mumNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="mum"]`);
        if (icon) {
          this.updateNameTabIcon('mum', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateMumIcon, 100);
        }
      };
      setTimeout(updateMumIcon, 100);
    }
    
    this.updateSaveButton();
  }
  
  /**
   * Selects first color and font by default if none selected.
   * @private
   */
  #selectFirstColorAndFontIfNotSelected() {
    const colorGrid = this.refs?.colorGrid || this.querySelector('.personalise-modal__color-grid');
    const fontGrid = this.refs?.fontGrid || this.querySelector('.personalise-modal__font-grid');

    if (colorGrid) {
      const hasColor = this.selectedColor || this.personalisationData?.color ||
        Array.from(colorGrid.querySelectorAll('input[type="radio"]')).some((input) => input.checked);
      if (!hasColor) {
        const firstRadio = colorGrid.querySelector('input[type="radio"]');
        const firstValue = firstRadio?.value || firstRadio?.closest('.personalise-modal__color-button')?.dataset?.color;
        if (firstValue) this.selectColorByName(firstValue);
      }
    }

    if (fontGrid) {
      const hasFont = this.selectedFont || this.personalisationData?.font;
      if (!hasFont) {
        const firstButton = fontGrid.querySelector('.personalise-modal__font-button');
        const firstFont = firstButton?.dataset?.font;
        if (firstFont) this.selectFontByName(firstFont);
      }
    }
  }

  /**
   * Initializes save button state when dialog opens.
   * @private
   */
  #initializeSaveButtonState() {
    setTimeout(() => {
      this.updateSaveButton();
    }, 100);
  }

  /**
   * @returns {'.'|'-'}
   */
  #getDobSeparator() {
    return this.dataset.dobSeparator === '.' ? '.' : '-';
  }

  /**
   * @param {string} stored
   * @returns {Object|null}
   */
  #parseDobStringToParts(stored) {
    const v = String(stored || '').trim();
    if (!v) return null;
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m) {
      return { dd: m[3], mm: m[2], yyyy: m[1] };
    }
    m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (m) {
      return { dd: m[1], mm: m[2], yyyy: m[3] };
    }
    m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v);
    if (m) {
      return { dd: m[1], mm: m[2], yyyy: m[3] };
    }
    return null;
  }

  /** @param {string} dd @param {string} mm @param {string} yyyy */
  #formatDobPropertyFromParts(dd, mm, yyyy) {
    const sep = this.#getDobSeparator();
    return `${dd}${sep}${mm}${sep}${yyyy}`;
  }

  /**
   * @param {string} inputValue
   * @returns {string}
   */
  #dateInputValueToDobProperty(inputValue) {
    const v = String(inputValue || '').trim();
    if (!v) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m) {
      return this.#formatDobPropertyFromParts(m[3], m[2], m[1]);
    }
    const parts = this.#parseDobStringToParts(v);
    if (parts) return this.#formatDobPropertyFromParts(parts.dd, parts.mm, parts.yyyy);
    return v;
  }

  /**
   * @param {string} stored
   * @returns {string}
   */
  #dobPropertyToDateInputValue(stored) {
    const parts = this.#parseDobStringToParts(stored);
    if (!parts) return '';
    return `${parts.yyyy}-${parts.mm}-${parts.dd}`;
  }

  /**
   * @param {string} stored
   * @returns {string}
   */
  #normalizeDobPropertyString(stored) {
    const parts = this.#parseDobStringToParts(stored);
    if (!parts) return String(stored || '').trim();
    return this.#formatDobPropertyFromParts(parts.dd, parts.mm, parts.yyyy);
  }

  /**
   * Refreshes DOB, time, and weight overlays.
   * @private
   */
  #updateCbOptionalFieldPreviewOverlays() {
    this.updateDobPreviewOverlay();
    this.updateTobPreviewOverlay();
    this.updateWeightPreviewOverlay();
  }

  /**
   * Applies design overlay size.
   * @param {Object|string} sizeJson
   * @private
   */
  #applyDesignOverlaySize(sizeJson) {
    const overlay = this.refs.previewDesignOverlay || this.querySelector('.personalise-modal__preview-design-overlay');
    if (!overlay) return;

    let size = { width: 25 };
    if (sizeJson) {
      try {
        size = typeof sizeJson === 'string' ? JSON.parse(sizeJson) : sizeJson;
      } catch {
        // keep default
      }
    }
    this._designOverlaySize = size;

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const widthPct = size.width != null ? Number(size.width) : 25;
    if (wrap) {
      overlay.style.width = `${wrap.clientWidth * (widthPct / 100)}px`;
      overlay.style.height = 'auto';
    }
  }

  /**
   * Dynamically aligns the design image and text overlay as a unified block anchored
   * around the product's configured vertical midpoint (fallback_y).
   * @private
   */
  #repositionDesignOverlay() {
    const overlay = this.refs.previewDesignOverlay || this.querySelector('.personalise-modal__preview-design-overlay');
    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    if (!overlay || overlay.style.display === 'none' || !wrap) return;

    const textOverlay = this.refs.previewTextOverlay || this.querySelector('.personalise-modal__preview-text-overlay');
    const hasText = textOverlay && textOverlay.textContent && textOverlay.textContent.trim().length > 0;
    const size = this._designOverlaySize || { width: 25, gap: 8 };
    const gapPx = size.gap != null ? Number(size.gap) : 8;

    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;

    // Standard global midpoint for pouches is 48% unless a product metafield overrides it
    const fy = size.fallback_y != null ? Number(size.fallback_y) : 48;
    const fx = size.fallback_x != null ? Number(size.fallback_x) : 50;

    const centerY = wrapH * (fy / 100);
    const centerX = wrapW * (fx / 100);

    if (hasText && textOverlay) {
      const patchRect = overlay.getBoundingClientRect();
      const textRect = textOverlay.getBoundingClientRect();

      const patchH = patchRect.height || (wrapH * 0.18);
      const textH = textRect.height || 18;
      const totalH = patchH + gapPx + textH;

      // Anchor top of group relative to target vertical midpoint
      const groupTop = centerY - (totalH * 0.45);

      overlay.style.left = `${Math.round(centerX)}px`;
      overlay.style.top = `${Math.round(groupTop)}px`;
      overlay.style.transform = 'translate(-50%, 0)';

      textOverlay.style.left = `${Math.round(centerX)}px`;
      textOverlay.style.top = `${Math.round(groupTop + patchH + gapPx)}px`;
      textOverlay.style.bottom = 'auto';
      textOverlay.style.transform = 'translate(-50%, 0)';
    } else {
      overlay.style.left = `${Math.round(centerX)}px`;
      overlay.style.top = `${Math.round(centerY)}px`;
      overlay.style.transform = 'translate(-50%, -50%)';
    }
  }

  /**
   * Updates dynamic personalization text overlay.
   */
  updateCbPreviewOverlay() {
    const imageUrl = this.dataset.cbPersonalizationImage;
    const positionJson = this.dataset.cbPersonalizationPosition;

    const overlay = (this.refs && this.refs.previewTextOverlay) || this.querySelector('.personalise-modal__preview-text-overlay');

    if (!imageUrl || !positionJson) {
      if (overlay) overlay.textContent = '';
      this.#updateCbOptionalFieldPreviewOverlays();
      this.#repositionDesignOverlay();
      return;
    }
    if (!overlay) {
      this.#updateCbOptionalFieldPreviewOverlays();
      this.#repositionDesignOverlay();
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateCbPreviewOverlay(), { once: true });
    }

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      this.#updateCbOptionalFieldPreviewOverlays();
      this.#repositionDesignOverlay();
      return;
    }
    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    let name = '';
    if (supported.babyName || supported.kidName || supported.mumName) {
      const activePanel = this.querySelector('.personalise-name-tabs__panel.is-active');
      const panelType = activePanel && activePanel.dataset.panel;
      if (panelType === 'baby') {
        const babyInput = (this.refs && this.refs.babyNameInput) || this.querySelector('[data-name-input="baby"]');
        name = (babyInput && babyInput.value) || (this.personalisationData && this.personalisationData.babyName) || '';
      } else if (panelType === 'kid') {
        const kidInput = (this.refs && this.refs.kidNameInput) || this.querySelector('[data-name-input="kid"]');
        name = (kidInput && kidInput.value) || (this.personalisationData && this.personalisationData.kidName) || '';
      } else if (panelType === 'mum') {
        const mumInput = (this.refs && this.refs.mumNameInput) || this.querySelector('[data-name-input="mum"]');
        name = (mumInput && mumInput.value) || (this.personalisationData && this.personalisationData.mumName) || '';
      }
      name = String(name).trim();
    } else {
      const nameInput = (this.refs && this.refs.nameInput) || this.querySelector('#personalise-name');
      const nameVal = (nameInput && nameInput.value) || (this.personalisationData && this.personalisationData.name) || '';
      name = String(nameVal).trim();
    }
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    overlay.textContent = name;
    overlay.style.fontSize = `${fontSize}px`;
    overlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    overlay.style.transform = 'translate(-50%, 50%)';
    overlay.style.top = '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';
    overlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';

    if (!wrap || !img || !img.naturalWidth) {
      this.#updateCbOptionalFieldPreviewOverlays();
      this.#repositionDesignOverlay();
      return;
    }
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) {
      this.#updateCbOptionalFieldPreviewOverlays();
      this.#repositionDesignOverlay();
      return;
    }
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    overlay.style.left = Math.round(leftPx) + 'px';
    overlay.style.bottom = Math.round(bottomPx) + 'px';
    this.#updateCbOptionalFieldPreviewOverlays();
    this.#repositionDesignOverlay();
  }

  /**
   * Updates DOB overlay.
   */
  updateDobPreviewOverlay() {
    const positionJson = this.dataset.cbDobPersonalizationPosition;
    if (!positionJson) {
      const dobOverlay = (this.refs && this.refs.previewDobOverlay) || this.querySelector('.personalise-modal__preview-dob-overlay');
      if (dobOverlay) {
        dobOverlay.style.display = 'none';
        dobOverlay.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateDobPreviewOverlay(), { once: true });
    }

    const dobOverlay = (this.refs && this.refs.previewDobOverlay) || this.querySelector('.personalise-modal__preview-dob-overlay');
    if (!dobOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      dobOverlay.style.display = 'none';
      return;
    }

    const dobInput = this.refs?.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    const optionalDobInput = this.refs?.optionalDobInput || this.querySelector('#dob_field_val');
    let dobValue = '';
    if (dobInput?.value?.trim()) {
      dobValue = this.#dateInputValueToDobProperty(dobInput.value.trim());
    } else if (optionalDobInput?.value) {
      dobValue = this.#dateInputValueToDobProperty(optionalDobInput.value);
    } else if (this.personalisationData?.dob) {
      dobValue = this.#normalizeDobPropertyString(this.personalisationData.dob);
    } else if (this.personalisationData?.optionalDob) {
      dobValue = this.#normalizeDobPropertyString(this.personalisationData.optionalDob);
    }

    if (!dobValue) {
      dobOverlay.style.display = 'none';
      dobOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    dobOverlay.textContent = dobValue;
    dobOverlay.style.fontSize = `${fontSize}px`;
    dobOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    dobOverlay.style.transform = 'translate(-50%, 50%)';
    dobOverlay.style.top = '';
    dobOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    dobOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    dobOverlay.style.left = Math.round(leftPx) + 'px';
    dobOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Updates time of birth overlay.
   */
  updateTobPreviewOverlay() {
    const positionJson = this.dataset.cbTobPersonalizationPosition;
    if (!positionJson) {
      const el = (this.refs && this.refs.previewTobOverlay) || this.querySelector('.personalise-modal__preview-tob-overlay');
      if (el) {
        el.style.display = 'none';
        el.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateTobPreviewOverlay(), { once: true });
    }

    const tobOverlay = (this.refs && this.refs.previewTobOverlay) || this.querySelector('.personalise-modal__preview-tob-overlay');
    if (!tobOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      tobOverlay.style.display = 'none';
      return;
    }

    const timeInput = this.refs?.timeInput || this.querySelector('input[name="properties[Time]"]');
    let timeValue =
      (timeInput?.value && String(timeInput.value).trim()) ||
      (this.personalisationData?.time ? String(this.personalisationData.time).trim() : '');

    if (!timeValue) {
      tobOverlay.style.display = 'none';
      tobOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    tobOverlay.textContent = timeValue;
    tobOverlay.style.fontSize = `${fontSize}px`;
    tobOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    tobOverlay.style.transform = 'translate(-50%, 50%)';
    tobOverlay.style.top = '';
    tobOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    tobOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    tobOverlay.style.left = Math.round(leftPx) + 'px';
    tobOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Updates weight overlay.
   */
  updateWeightPreviewOverlay() {
    const positionJson = this.dataset.cbWeightPersonalizationPosition;
    if (!positionJson) {
      const el = (this.refs && this.refs.previewWeightOverlay) || this.querySelector('.personalise-modal__preview-weight-overlay');
      if (el) {
        el.style.display = 'none';
        el.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateWeightPreviewOverlay(), { once: true });
    }

    const weightOverlay = (this.refs && this.refs.previewWeightOverlay) || this.querySelector('.personalise-modal__preview-weight-overlay');
    if (!weightOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      weightOverlay.style.display = 'none';
      return;
    }

    const weightInput = this.refs?.weightInput || this.querySelector('input[name="properties[Weight]"]');
    let weightValue =
      (weightInput?.value && String(weightInput.value).trim()) ||
      (this.personalisationData?.weight ? String(this.personalisationData.weight).trim() : '');

    if (!weightValue) {
      weightOverlay.style.display = 'none';
      weightOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    weightOverlay.textContent = weightValue;
    weightOverlay.style.fontSize = `${fontSize}px`;
    weightOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    weightOverlay.style.transform = 'translate(-50%, 50%)';
    weightOverlay.style.top = '';
    weightOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    weightOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    weightOverlay.style.left = Math.round(leftPx) + 'px';
    weightOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Handles name input changes.
   * @param {Event} event
   */
  handleNameInput = (event) => {
    const input = event.target;
    const value = input.value;

    const length = value.length;
    this.updateCharCounter(length);
    this.personalisationData.name = value;
    
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  };

  /**
   * Handles design text input change (legacy/fallback).
   * @param {Event} event
   */
  handleDesignInput = (event) => {
    const typed = event.target.value.trim().toLowerCase();
    if (typed) {
      this.selectDesignByName(typed);
    }
  };

  /**
   * Handles design swatch selection click (supports toggle deselect).
   * @param {Event} event
   */
  selectDesign = (event) => {
    const button = event.target.closest('.personalise-modal__design-button');
    if (!button) return;

    const radio = button.querySelector('input[type="radio"]');
    // Toggle off if clicking an already selected design
    if (radio && radio.checked && button.classList.contains('personalise-modal__design-button--selected')) {
      radio.checked = false;
      button.classList.remove('personalise-modal__color-button--selected', 'personalise-modal__design-button--selected');
      this.personalisationData.design = null;
      
      const hiddenInput = this.refs.designInput || this.querySelector('#personalise-design');
      if (hiddenInput) hiddenInput.value = '';

      const overlay = this.refs.previewDesignOverlay || this.querySelector('.personalise-modal__preview-design-overlay');
      if (overlay) {
        overlay.style.display = 'none';
        overlay.removeAttribute('src');
      }

      this.updateSaveButton();
      this.updateCbPreviewOverlay();
      return;
    }

    const designName = button.dataset.design || radio?.value;
    this.selectDesignByName(designName);
  };

  /**
   * Selects a design swatch by name.
   * @param {string} designName
   */
  selectDesignByName(designName) {
    const designGrid = this.refs.designGrid || this.querySelector('.personalise-modal__design-grid');
    if (!designGrid || !designName) return;

    const radioInputs = designGrid.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.checked = false;
    });
    designGrid.querySelectorAll('.personalise-modal__design-button').forEach((l) => l.classList.remove('personalise-modal__color-button--selected', 'personalise-modal__design-button--selected'));

    const designNameLower = String(designName).toLowerCase().trim();
    const cleanKey = designNameLower.replace(/\s+/g, '');

    const selectedButton = Array.from(designGrid.querySelectorAll('.personalise-modal__design-button')).find(
      btn => {
        const btnDesign = (btn.dataset.design || '').toLowerCase().trim();
        const btnDesignKey = (btn.dataset.designKey || '').toLowerCase().trim();
        const radioValue = (btn.querySelector('input[type="radio"]')?.value || '').toLowerCase().trim();
        return btnDesign === designNameLower || btnDesignKey === cleanKey || (radioValue && radioValue === designNameLower);
      }
    );

    if (selectedButton) {
      const radioInput = selectedButton.querySelector('input[type="radio"]');
      if (radioInput) {
        radioInput.checked = true;
      }
      selectedButton.classList.add('personalise-modal__color-button--selected', 'personalise-modal__design-button--selected');
      const storedDesign = radioInput?.value || selectedButton.dataset.design || designName;
      this.personalisationData.design = storedDesign;

      const hiddenInput = this.refs.designInput || this.querySelector('#personalise-design');
      if (hiddenInput) {
        hiddenInput.value = storedDesign;
      }

      const map = this.#getDesignsMap();
      const overlay = this.refs.previewDesignOverlay || this.querySelector('.personalise-modal__preview-design-overlay');
      if (map && overlay) {
        const match = map.designs.find((d) => d.key.replace(/\s+/g, '') === cleanKey || d.label.toLowerCase().trim() === storedDesign.toLowerCase().trim());
        if (match) {
          overlay.src = match.image;
          overlay.alt = match.label;
          overlay.style.display = 'block';
          this.#applyDesignOverlaySize(map.size);
          this.#repositionDesignOverlay();
          if (!overlay.complete) {
            overlay.addEventListener('load', () => this.#repositionDesignOverlay(), { once: true });
          }
        }
      }
    } else {
      const hiddenInput = this.refs.designInput || this.querySelector('#personalise-design');
      if (hiddenInput) {
        hiddenInput.value = designName;
      }
      this.personalisationData.design = designName;
    }

    this.updateSaveButton();
  }

  /**
   * Handles name tab input changes.
   * @param {Event} event
   */
  handleNameTabInput = (event) => {
    const input = event.target;
    const tabType = input.dataset.nameInput;

    const value = input.value.trim();
    const length = input.value.length;
    
    this.updateNameTabCharCounter(tabType, length);
    
    if (tabType === 'baby') {
      this.personalisationData.babyName = value;
    } else if (tabType === 'kid') {
      this.personalisationData.kidName = value;
    } else if (tabType === 'mum') {
      this.personalisationData.mumName = value;
    }
    
    this.updateNameTabIcon(tabType, value.length > 0);
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  };
  
  /**
   * Updates name tab character counter.
   * @param {string} tabType
   * @param {number} count
   */
  updateNameTabCharCounter(tabType, count) {
    let charCounter = null;
    if (tabType === 'baby') {
      charCounter = this.refs.babyCharCounter?.querySelector('[ref="babyCharCount"]') || this.refs.babyCharCount;
    } else if (tabType === 'kid') {
      charCounter = this.refs.kidCharCounter?.querySelector('[ref="kidCharCount"]') || this.refs.kidCharCount;
    } else if (tabType === 'mum') {
      charCounter = this.refs.mumCharCounter?.querySelector('[ref="mumCharCount"]') || this.refs.mumCharCount;
    }
    
    if (charCounter) {
      charCounter.textContent = count;
    }
  }

  /**
   * Switches name tabs.
   * @param {Event} event
   */
  switchNameTab = (event) => {
    const button = event.target.closest('.personalise-name-tabs__tab');
    if (!button) return;
    
    const tabType = button.dataset.tab;
    const tabsContainer = this.querySelector('.personalise-name-tabs__tablist');
    const panelsContainer = this.querySelector('.personalise-name-tabs__panels');
    
    if (!tabsContainer || !panelsContainer) return;
    
    const allTabs = tabsContainer.querySelectorAll('.personalise-name-tabs__tab');
    allTabs.forEach(tab => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
    });
    
    button.classList.add('is-active');
    button.setAttribute('aria-selected', 'true');
    
    const allPanels = panelsContainer.querySelectorAll('.personalise-name-tabs__panel');
    allPanels.forEach(panel => {
      panel.classList.remove('is-active');
    });
    
    const activePanel = panelsContainer.querySelector(`[data-panel="${tabType}"]`);
    if (activePanel) {
      activePanel.classList.add('is-active');
    }
    this.updateCbPreviewOverlay();
  };

  /**
   * Updates tick icon on name tab.
   * @param {string} tabType
   * @param {boolean} show
   */
  updateNameTabIcon(tabType, show) {
    const icon = this.querySelector(`[data-tab-icon="${tabType}"]`);
    if (icon) {
      icon.style.display = show ? 'inline-flex' : 'none';
    } else {
      console.warn(`Tab icon not found for type: ${tabType}`, this.querySelectorAll('[data-tab-icon]'));
    }
  }

  /**
   * Updates character counter.
   * @param {number} count
   */
  updateCharCounter(count) {
    const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
    if (charCounter) {
      charCounter.textContent = count;
    }
  }

  /**
   * Handles color selection click.
   * @param {Event} event
   */
  selectColor = (event) => {
    const button = event.target.closest('.personalise-modal__color-button');
    if (!button) return;

    const colorName = button.dataset.color || button.querySelector('input[type="radio"]')?.value;
    this.selectColorByName(colorName);
  };

  /**
   * Selects color by name.
   * @param {string} colorName
   */
  selectColorByName(colorName) {
    const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
    if (!colorGrid || !colorName) return;

    const radioInputs = colorGrid.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.checked = false;
    });
    colorGrid.querySelectorAll('.personalise-modal__color-button').forEach((l) => l.classList.remove('personalise-modal__color-button--selected'));

    const colorNameLower = String(colorName).toLowerCase().trim();
    const selectedButton = Array.from(colorGrid.querySelectorAll('.personalise-modal__color-button')).find(
      btn => {
        const btnColor = (btn.dataset.color || '').toLowerCase();
        const radioValue = (btn.querySelector('input[type="radio"]')?.value || '').toLowerCase();
        return btnColor === colorNameLower || (radioValue && radioValue === colorNameLower);
      }
    );
    if (selectedButton) {
      const radioInput = selectedButton.querySelector('input[type="radio"]');
      if (radioInput) {
        radioInput.checked = true;
      }
      selectedButton.classList.add('personalise-modal__color-button--selected');
      const storedColor = radioInput?.value || colorName;
      this.selectedColor = storedColor;
      this.personalisationData.color = storedColor;
    }
    
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  }

  /**
   * Handles font selection click.
   * @param {Event} event
   */
  selectFont = (event) => {
    const button = event.target.closest('.personalise-modal__font-button');
    if (!button) return;

    const fontName = button.dataset.font;
    this.selectFontByName(fontName);
  };

  /**
   * Selects font by name.
   * @param {string} fontName
   */
  selectFontByName(fontName) {
    if (!this.refs.fontGrid) return;

    const buttons = this.refs.fontGrid.querySelectorAll('.personalise-modal__font-button');
    buttons.forEach(btn => {
      btn.classList.remove('personalise-modal__font-button--selected');
    });

    const selectedButton = Array.from(buttons).find(
      btn => btn.dataset.font === fontName
    );
    if (selectedButton) {
      selectedButton.classList.add('personalise-modal__font-button--selected');
      this.selectedFont = fontName;
      this.personalisationData.font = fontName;
    }
    
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  }

  /**
   * Updates save button state.
   */
  updateSaveButton() {
    if (this.refs.saveButton) {
      const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
      const hasName = nameInput ? nameInput.value.trim().length > 0 : true;
      
      const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
      const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
      const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
      
      const hasBabyName = babyNameInput ? babyNameInput.value.trim().length > 0 : true;
      const hasKidName = kidNameInput ? kidNameInput.value.trim().length > 0 : true;
      const hasMumName = mumNameInput ? mumNameInput.value.trim().length > 0 : true;
      
      const babyNameRequired = babyNameInput && !hasBabyName;
      const kidNameRequired = kidNameInput && !hasKidName;
      const mumNameRequired = mumNameInput && !hasMumName;
      
      const fontGrid = this.refs.fontGrid || this.querySelector('.personalise-modal__font-grid');
      const hasFont = fontGrid ? (this.selectedFont || this.personalisationData.font) : true;
      const fontRequired = fontGrid && !hasFont;
      
      const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
      const colorRadioInputs = colorGrid ? colorGrid.querySelectorAll('input[type="radio"]') : [];
      const hasColor = colorGrid ? (this.selectedColor || this.personalisationData.color || Array.from(colorRadioInputs).some(input => input.checked)) : true;
      const colorRequired = colorGrid && colorRadioInputs.length > 0 && !hasColor;

      // Keep design selection optional
      const designRequired = false;
      
      const allInputs = this.querySelectorAll('input, textarea, select');
      let hasMissingRequiredField = false;
      allInputs.forEach(input => {
        if (input.type === 'radio' && !input.checked) {
          return;
        }
        if (input.hasAttribute('required') || input.hasAttribute('aria-required')) {
          if ((input.type === 'text' || input.type === 'textarea' || input.tagName === 'TEXTAREA' || input.tagName === 'SELECT') && !input.value.trim()) {
            hasMissingRequiredField = true;
          }
          if (input.type === 'checkbox' && !input.checked) {
            hasMissingRequiredField = true;
          }
        }
      });
      
      if (nameInput && !hasName) {
        this.refs.saveButton.disabled = true;
      } else if (babyNameRequired || kidNameRequired || mumNameRequired) {
        this.refs.saveButton.disabled = true;
      } else if (fontRequired) {
        this.refs.saveButton.disabled = true;
      } else if (colorRequired) {
        this.refs.saveButton.disabled = true;
      } else if (designRequired) {
        this.refs.saveButton.disabled = true;
      } else if (hasMissingRequiredField) {
        this.refs.saveButton.disabled = true;
      } else {
        this.refs.saveButton.disabled = false;
      }
    }
  }

  /**
   * Saves personalisation and submits form or updates cart item.
   * @param {Event} event
   */
  savePersonalisation = async (event) => {
    event.preventDefault();
    
    const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
    const name = nameInput ? nameInput.value.trim() : '';
    
    if (nameInput && !name) {
      return;
    }

    const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
    const babyNameValue = babyNameInput && babyNameInput.value.trim() ? babyNameInput.value.trim() : null;
    
    const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
    const kidNameValue = kidNameInput && kidNameInput.value.trim() ? kidNameInput.value.trim() : null;
    
    const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
    const mumNameValue = mumNameInput && mumNameInput.value.trim() ? mumNameInput.value.trim() : null;

    const supported = this.#getSupportedPersonalisationFields();
    const personalisation = {
      name: supported.name ? name : null,
      font: supported.font ? (this.personalisationData.font || this.selectedFont) : null,
      color: supported.color ? (this.personalisationData.color || this.selectedColor) : null,
      design: supported.design ? (this.personalisationData.design || null) : null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: supported.babyName ? babyNameValue : null,
      kidName: supported.kidName ? kidNameValue : null,
      mumName: supported.mumName ? mumNameValue : null
    };

    const dobInput = this.refs.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && dobInput.value.trim()) {
      personalisation.dob = this.#dateInputValueToDobProperty(dobInput.value.trim());
    }

    const schoolYearInput = this.refs.schoolYearInput || this.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput) personalisation.schoolYear = schoolYearInput.value.trim();

    const name1Input = this.refs.name1Input || this.querySelector('input[name="properties[Name 1]"]');
    if (name1Input) personalisation.name1 = name1Input.value.trim();

    const name2Input = this.refs.name2Input || this.querySelector('input[name="properties[Name 2]"]');
    if (name2Input) personalisation.name2 = name2Input.value.trim();

    const name3Input = this.refs.name3Input || this.querySelector('input[name="properties[Name 3]"]');
    if (name3Input) personalisation.name3 = name3Input.value.trim();

    const name4Input = this.refs.name4Input || this.querySelector('input[name="properties[Name 4]"]');
    if (name4Input) personalisation.name4 = name4Input.value.trim();

    const textboxInput = this.refs.textboxInput || this.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput) personalisation.textbox = textboxInput.value.trim();

    const messageInput = this.refs.messageInput || this.querySelector('textarea[name="properties[Message]"]');
    if (messageInput) personalisation.message = messageInput.value.trim();

    const optionalDobInput = this.refs.optionalDobInput || this.querySelector('#dob_field_val');
    if (optionalDobInput && optionalDobInput.value) {
      personalisation.optionalDob = this.#dateInputValueToDobProperty(optionalDobInput.value);
    }

    const timeInput = this.refs.timeInput || this.querySelector('input[name="properties[Time]"]');
    if (timeInput) personalisation.time = timeInput.value.trim();

    const weightInput = this.refs.weightInput || this.querySelector('input[name="properties[Weight]"]');
    if (weightInput) personalisation.weight = weightInput.value.trim();

    if (babyNameValue !== null) this.personalisationData.babyName = babyNameValue;
    if (kidNameValue !== null) this.personalisationData.kidName = kidNameValue;
    if (mumNameValue !== null) this.personalisationData.mumName = mumNameValue;

    const filteredPersonalisation = this.#filterToSupportedFields(personalisation);

    const cartContext = window.cartPersonalizationContext;
    let form = null;
    
    if (cartContext && cartContext.cartLine) {
      await this.#updateCartItemProperties(cartContext, filteredPersonalisation);
      
      form = this.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) {
        const quickAddModal = document.querySelector('#quick-add-modal-content');
        if (quickAddModal) {
          form = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
        }
      }
      if (!form) {
        form = document.querySelector('form[data-type="add-to-cart-form"]');
      }
      
    } else {
      form = this.#getProductForm();

      if (form) {
        this.updateFormFields(filteredPersonalisation);
      }

      const productId = form?.closest('product-form-component')?.dataset?.productId;
      const variantId = form?.querySelector('input[name="id"]')?.value;
      if (!window.currentPersonalisation) {
        window.currentPersonalisation = {};
      }
      if (variantId) {
        window.currentPersonalisation[variantId] = filteredPersonalisation;
      }
      if (productId) {
        window.currentPersonalisation[productId] = filteredPersonalisation;
      }

      if (filteredPersonalisation.color && form) {
        const allForms = this.#getAllProductForms();
        allForms.forEach((f) => f.removeAttribute('data-personalisation-color-required'));
        const formComp = form.closest('product-form-component');
        const pid = formComp?.dataset?.productId || this.dataset?.productId;
        const searchRoots = [
          formComp,
          form.closest('#quick-add-modal-content'),
          pid ? document.querySelector(`sticky-add-to-cart[data-product-id="${pid}"]`) : null
        ].filter(Boolean);
        for (const root of searchRoots) {
          const msgEl = root.querySelector('[data-personalise-color-required-msg]');
          if (msgEl) msgEl.style.display = 'none';
          const confirmation = root.querySelector('[data-personalise-confirmation]');
          if (confirmation) confirmation.style.display = '';
        }
        if (typeof window.updateAddToCartButtonState === 'function') {
          window.updateAddToCartButtonState();
        }
      }
    }

    const formComponent = form?.closest('product-form-component');
    const productId = formComponent?.dataset?.productId || this.dataset?.productId;
    
    const customEvent = new CustomEvent('personalisation-saved', {
      detail: {
        ...filteredPersonalisation,
        form: form,
        formId: form?.id,
        productId: productId,
        formComponent: formComponent
      },
      bubbles: true,
      cancelable: true
    });
    this.dispatchEvent(customEvent);
    document.dispatchEvent(customEvent);

    const isAddToCartMode = this.refs?.saveButton?.dataset?.addToCartMode === 'true';
    if (isAddToCartMode && form && !cartContext) {
      const saveButton = this.refs.saveButton;
      const addToCartText = saveButton?.dataset?.addToCartText || 'Add to cart';
      const addingText = saveButton?.dataset?.addingText || 'Adding...';

      if (saveButton) {
        saveButton.disabled = true;
        saveButton.textContent = addingText;
      }

      const feeVariantId = this.dataset.designFeeVariantId || '46804856733775';
      const mainVariantId = form.querySelector('input[name="id"]')?.value;

      if (filteredPersonalisation.design && feeVariantId && mainVariantId) {
        try {
          const itemsToAdd = [
            {
              id: Number(mainVariantId),
              quantity: 1,
              properties: filteredPersonalisation
            },
            {
              id: Number(feeVariantId),
              quantity: 1,
              properties: {
                'For Item': filteredPersonalisation.name || 'Personalised Product',
                'Design Patch': filteredPersonalisation.design
              }
            }
          ];

          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });

          document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, { bubbles: true, detail: { data: { didError: false } } }));
          this.closeDialog();
          return;
        } catch (error) {
          console.error('Error adding multi-item personalisation payload:', error);
          if (saveButton) {
            saveButton.disabled = false;
            saveButton.textContent = addToCartText;
          }
          return;
        }
      }

      const confirmCheckbox = formComponent?.querySelector('[data-personalise-confirm-checkbox]');
      if (confirmCheckbox) {
        confirmCheckbox.checked = true;
      }

      form.requestSubmit();
    } else {
      this.closeDialog();
    }

    if (cartContext) {
      window.cartPersonalizationContext = null;
    }
  };

  /**
   * Updates cart item properties using cart/change.js
   * @param {Object} cartContext
   * @param {Object} personalisation
   * @private
   */
  async #updateCartItemProperties(cartContext, personalisation) {
    try {
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      const lineIndex = parseInt(cartContext.cartLine) - 1;
      const currentCartItem = currentCart.items[lineIndex];
      
      if (!currentCartItem) {
        throw new Error('Cart item not found');
      }
      
      const currentQuantity = currentCartItem.quantity || 1;
      const properties = { ...(currentCartItem.properties || {}) };

      const setOrDelete = (key, value) => {
        if (value && String(value).trim() !== '') {
          properties[key] = String(value).trim();
        } else if (key in properties) {
          delete properties[key];
        }
      };

      setOrDelete('Name', personalisation.name);
      setOrDelete('Name 1', personalisation.name1);
      setOrDelete('Name 2', personalisation.name2);
      setOrDelete('Name 3', personalisation.name3);
      setOrDelete('Name 4', personalisation.name4);
      setOrDelete("Baby's Name", personalisation.babyName);
      setOrDelete("Kid's Name", personalisation.kidName);
      setOrDelete("Mum's Name", personalisation.mumName);
      setOrDelete('Date of Birth', personalisation.dob);
      setOrDelete('Personalise Date of Birth', personalisation.optionalDob);
      setOrDelete('School Year', personalisation.schoolYear);
      setOrDelete('Text Colour', personalisation.color);
      setOrDelete('Text Font', personalisation.font);
      setOrDelete('Design', personalisation.design);
      setOrDelete('Personalisation:', personalisation.textbox);
      setOrDelete('Message', personalisation.message);
      setOrDelete('Time', personalisation.time);
      setOrDelete('Weight', personalisation.weight);
      
      const feeVariantId = this.dataset.designFeeVariantId || '46804856733775';
      if (personalisation.design && feeVariantId) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(feeVariantId),
              quantity: 1,
              properties: {
                'For Item': personalisation.name || 'Personalised Product',
                'Design Patch': personalisation.design
              }
            }]
          })
        });
      }

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIdSet = new Set();
      cartItemsComponents.forEach(comp => {
        if (comp.dataset?.sectionId) {
          sectionIdSet.add(comp.dataset.sectionId);
        }
      });
      const sectionIds = Array.from(sectionIdSet).join(',');
      
      const body = {
        line: cartContext.cartLine,
        quantity: currentQuantity,
        properties: properties
      };
      
      if (sectionIds) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.statusText}`);
      }
      
      const cart = await response.json();
      
      if (cart.sections) {
        Object.keys(cart.sections).forEach(sectionId => {
          if (cart.sections[sectionId]) {
            morphSection(sectionId, cart.sections[sectionId]);
          }
        });
      } else if (sectionIds) {
        const sectionsResponse = await fetch(`/?sections=${sectionIds}`);
        const sectionsData = await sectionsResponse.json();
        
        Object.keys(sectionsData).forEach(sectionId => {
          if (sectionsData[sectionId]) {
            morphSection(sectionId, sectionsData[sectionId]);
          }
        });
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error updating cart item properties:', error);
    }
  }

  /**
   * Updates form fields with personalisation data.
   * @param {Object} personalisation
   */
  updateFormFields(personalisation) {
    const filtered = this.#filterToSupportedFields(personalisation || {});
    if (Object.keys(filtered).length === 0) return;

    let productForm = null;
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    if (!productForm) {
      productForm = document.querySelector(`form[data-type="add-to-cart-form"]`);
    }
    
    if (!productForm) return;

    const existingProps = productForm.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
    existingProps.forEach(input => {
      const name = input.name;
      if (name.includes('[Name]') || 
          name.includes('[Text Font]') || 
          name.includes('[Text Colour]') || 
          name.includes('[Design]') || 
          name.includes('[Date of Birth]') || 
          name.includes('[School Year]') || 
          name.includes('[Name 1]') || 
          name.includes('[Name 2]') || 
          name.includes('[Name 3]') || 
          name.includes('[Name 4]') || 
          name.includes('[Personalisation:]') || 
          name.includes('[Personalise Date of Birth]') || 
          name.includes('[Time]') || 
          name.includes('[Weight]') ||
          name.includes('[Baby\'s Name]') ||
          name.includes('[Kid\'s Name]') ||
          name.includes('[Mum\'s Name]') ||
          name.includes('[Message]')) {
        input.remove();
      }
    });

    const addProperty = (name, value) => {
      if (value && value.toString().trim() !== '') {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value.toString().trim();
        productForm.appendChild(input);
      }
    };

    if (filtered.name) addProperty('properties[Name]', filtered.name);
    if (filtered.font) addProperty('properties[Text Font]', filtered.font);
    if (filtered.color) addProperty('properties[Text Colour]', filtered.color);
    if (filtered.design) addProperty('properties[Design]', filtered.design);
    if (filtered.dob) addProperty('properties[Date of Birth]', filtered.dob);
    if (filtered.schoolYear) addProperty('properties[School Year]', filtered.schoolYear);
    if (filtered.name1) addProperty('properties[Name 1]', filtered.name1);
    if (filtered.name2) addProperty('properties[Name 2]', filtered.name2);
    if (filtered.name3) addProperty('properties[Name 3]', filtered.name3);
    if (filtered.name4) addProperty('properties[Name 4]', filtered.name4);
    if (filtered.textbox) addProperty('properties[Personalisation:]', filtered.textbox);
    if (filtered.message) addProperty('properties[Message]', filtered.message);
    if (filtered.optionalDob) addProperty('properties[Personalise Date of Birth]', filtered.optionalDob);
    if (filtered.time) addProperty('properties[Time]', filtered.time);
    if (filtered.weight) addProperty('properties[Weight]', filtered.weight);
    if (filtered.babyName) addProperty('properties[Baby\'s Name]', filtered.babyName);
    if (filtered.kidName) addProperty('properties[Kid\'s Name]', filtered.kidName);
    if (filtered.mumName) addProperty('properties[Mum\'s Name]', filtered.mumName);
    
    this.setupFormSubmitListener(productForm);
  }

  /**
   * Intercepts product form submission on the product page and submits a dual-item
   * JSON payload (/cart/add.js) when a design patch is selected.
   * @param {HTMLFormElement} form
   */
  setupFormSubmitListener(form) {
    if (form.dataset.personalisationListenerAdded) return;
    form.dataset.personalisationListenerAdded = 'true';

    const dialogComponent = this;

    const handleDualItemSubmit = async (event) => {
      const feeVariantId = dialogComponent.dataset?.designFeeVariantId || '46804856733775';
      const mainVariantId = form.querySelector('input[name="id"]')?.value;

      // Check for an active design selection
      let designValue = '';
      const designInput = form.querySelector('input[name="properties[Design]"]') || dialogComponent.querySelector('#personalise-design');
      if (designInput && designInput.value.trim()) {
        designValue = designInput.value.trim();
      } else if (dialogComponent.personalisationData?.design) {
        designValue = dialogComponent.personalisationData.design;
      }

      // Intercept and send dual items if design fee applies
      if (designValue && feeVariantId && mainVariantId) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const properties = {};
        const formData = new FormData(form);
        for (let [key, val] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.replace('properties[', '').replace(']', '');
            if (val && String(val).trim()) {
              properties[propName] = String(val).trim();
            }
          }
        }

        const itemsToAdd = [
          {
            id: Number(mainVariantId),
            quantity: 1,
            properties: properties
          },
          {
            id: Number(feeVariantId),
            quantity: 1,
            properties: {
              'For Item': properties['Name'] || properties["Baby's Name"] || properties["Kid's Name"] || 'Personalised Product',
              'Design Patch': designValue
            }
          }
        ];

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
          const cartData = await response.json();

          // Dispatch theme drawer refresh events
          document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate || 'cart:update', { bubbles: true, detail: { data: cartData } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true }));

          // Open cart drawer or fallback to cart page
          const cartDrawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show();
          } else {
            window.location.href = '/cart';
          }
        } catch (error) {
          console.error('Multi-item cart submission error:', error);
        }

        return false;
      }
    };

    // Attach listener at capture phase to precede theme AJAX scripts
    form.addEventListener('submit', handleDualItemSubmit, { capture: true });
  }
}

customElements.define('personalise-dialog', PersonaliseDialogComponent);